"use client";

import { useState, useEffect, useCallback } from "react";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Vehicle, OneTimePackage } from "@/types";
import { VehicleMultiSelect } from "@/components/appointment/VehicleMultiSelect";
import { repeatedAppointmentService } from "@/services/repeatedAppointmentService";
import {
  RepeatedAppointmentWithDetails,
  WEEKDAYS,
} from "@/types/repeatedAppointment.types";
import { generateTimeSlots, formatCurrency } from "@/lib/utils";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Edit2, Package } from "lucide-react";

const timeSlots = generateTimeSlots();

interface RepeatedAppointmentSetupProps {
  customerId: string;
  customerVehicles: Vehicle[];
  packages: OneTimePackage[];
}

export function RepeatedAppointmentSetup({
  customerId,
  customerVehicles,
  packages,
}: RepeatedAppointmentSetupProps) {
  // Existing setups from backend
  const [existingSetups, setExistingSetups] = useState<
    RepeatedAppointmentWithDetails[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state for new/editing setup
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedPackageId, setSelectedPackageId] = useState("");
  const [selectedVehicleIds, setSelectedVehicleIds] = useState<string[]>([]);
  const [preferredDay, setPreferredDay] = useState("");
  const [preferredTime, setPreferredTime] = useState("");
  const [vehicleSearchTerm, setVehicleSearchTerm] = useState("");

  // Fetch existing setups
  const fetchSetups = useCallback(async () => {
    if (!customerId) return;

    setLoading(true);
    try {
      const setups =
        await repeatedAppointmentService.getByCustomerId(customerId);
      setExistingSetups(setups);
    } catch (error) {
      console.error("Error fetching repeated appointments:", error);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    fetchSetups();
  }, [fetchSetups]);

  const resetForm = () => {
    setSelectedPackageId("");
    setSelectedVehicleIds([]);
    setPreferredDay("");
    setPreferredTime("");
    setVehicleSearchTerm("");
    setIsEditing(false);
    setEditingId(null);
  };

  const handleStartNew = () => {
    resetForm();
    setIsEditing(true);
  };

  const handleEdit = (setup: RepeatedAppointmentWithDetails) => {
    setEditingId(setup.id);
    setSelectedPackageId(setup.packageId);
    setSelectedVehicleIds(setup.vehicleIds);
    setPreferredDay(setup.preferredDay || "");
    setPreferredTime(setup.preferredTime || "");
    setIsEditing(true);
  };

  const handleCancel = () => {
    resetForm();
  };

  const handleSave = async () => {
    if (!selectedPackageId) {
      toast.error("Please select a package");
      return;
    }
    if (selectedVehicleIds.length === 0) {
      toast.error("Please select at least one vehicle");
      return;
    }
    if (!preferredDay) {
      toast.error("Please select a preferred day");
      return;
    }
    if (!preferredTime) {
      toast.error("Please select a preferred time");
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        // Update existing
        await repeatedAppointmentService.update(editingId, {
          packageId: selectedPackageId,
          vehicleIds: selectedVehicleIds,
          preferredDay,
          preferredTime,
        });
        toast.success("Recurring setup updated successfully");
      } else {
        // Create new
        await repeatedAppointmentService.create({
          customerId,
          packageId: selectedPackageId,
          vehicleIds: selectedVehicleIds,
          preferredDay,
          preferredTime,
        });
        toast.success("Recurring setup created successfully");
      }

      resetForm();
      await fetchSetups();
    } catch (error: any) {
      const message =
        error?.response?.data?.message || "Failed to save recurring setup";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this recurring setup?")) {
      return;
    }

    try {
      await repeatedAppointmentService.delete(id);
      toast.success("Recurring setup deleted");
      await fetchSetups();
    } catch (error) {
      toast.error("Failed to delete recurring setup");
    }
  };

  const selectedPackage = packages.find((p) => p.id === selectedPackageId);
  const activePackages = packages.filter((p) => p.active !== false);

  return (
    <div className="space-y-4">
      <Separator className="my-4" />

      {/* Existing Setups */}
      {loading ? (
        <div className="flex items-center gap-2 py-4">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm text-muted-foreground">
            Loading recurring setups...
          </span>
        </div>
      ) : existingSetups.length > 0 && !isEditing ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">Existing Recurring Setups</h4>
            <Button variant="outline" size="sm" onClick={handleStartNew}>
              <Plus className="h-4 w-4 mr-1" />
              Add New
            </Button>
          </div>
          {existingSetups.map((setup) => (
            <Card key={setup.id} className="p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">
                      {setup.package?.name || "Package"}
                    </span>
                    <Badge variant="secondary">
                      {formatCurrency(setup.package?.price || 0)}
                    </Badge>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {setup.preferredDay &&
                      `Every ${setup.preferredDay.charAt(0).toUpperCase()}${setup.preferredDay.slice(1)}`}
                    {setup.preferredTime && ` at ${setup.preferredTime}`}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {setup.vehicles?.map((v) => (
                      <Badge
                        key={v.id}
                        variant="outline"
                        className="text-xs font-normal"
                      >
                        {v.plateNumber}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEdit(setup)}
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(setup.id)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : null}

      {/* New/Edit Form */}
      {isEditing || existingSetups.length === 0 ? (
        <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">
              {editingId ? "Edit Recurring Setup" : "New Recurring Setup"}
            </h4>
            {existingSetups.length > 0 && (
              <Button variant="ghost" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
            )}
          </div>

          {/* Package Selection */}
          <div className="grid gap-2">
            <Label>Package *</Label>
            <Select
              value={selectedPackageId}
              onValueChange={setSelectedPackageId}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select package" />
              </SelectTrigger>
              <SelectContent>
                {activePackages.length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    No packages available
                  </div>
                ) : (
                  activePackages.map((pkg) => (
                    <SelectItem key={pkg.id} value={pkg.id}>
                      <div className="flex items-center gap-2">
                        <span>{pkg.name}</span>
                        <Badge variant="secondary" className="ml-auto">
                          {formatCurrency(pkg.price)}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {selectedPackage && (
              <p className="text-xs text-muted-foreground">
                {selectedPackage.description}
              </p>
            )}
          </div>

          {/* Vehicle Selection */}
          <div className="grid gap-2">
            <Label>Vehicles *</Label>
            <VehicleMultiSelect
              vehicles={customerVehicles}
              selectedVehicleIds={selectedVehicleIds}
              onSelectionChange={setSelectedVehicleIds}
              searchTerm={vehicleSearchTerm}
              onSearchChange={setVehicleSearchTerm}
              error={
                selectedVehicleIds.length === 0
                  ? "Select at least one vehicle"
                  : undefined
              }
            />
          </div>

          {/* Day and Time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Preferred Day *</Label>
              <Select value={preferredDay} onValueChange={setPreferredDay}>
                <SelectTrigger>
                  <SelectValue placeholder="Select day" />
                </SelectTrigger>
                <SelectContent>
                  {WEEKDAYS.map((day) => (
                    <SelectItem key={day.value} value={day.value}>
                      {day.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label>Preferred Time *</Label>
              <Select value={preferredTime} onValueChange={setPreferredTime}>
                <SelectTrigger>
                  <SelectValue placeholder="Select time" />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map((slot) => (
                    <SelectItem key={slot.value} value={slot.label}>
                      {slot.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Summary */}
          {selectedPackageId && selectedVehicleIds.length > 0 && (
            <div className="p-3 bg-primary/5 rounded-lg text-sm">
              <p className="font-medium">Summary:</p>
              <p className="text-muted-foreground">
                {selectedPackage?.name} for {selectedVehicleIds.length} vehicle
                {selectedVehicleIds.length !== 1 ? "s" : ""}
                {preferredDay &&
                  ` every ${preferredDay.charAt(0).toUpperCase()}${preferredDay.slice(1)}`}
                {preferredTime && ` at ${preferredTime}`}
              </p>
              <p className="font-medium mt-1">
                Amount per appointment:{" "}
                {formatCurrency(
                  (selectedPackage?.price || 0) * selectedVehicleIds.length
                )}
              </p>
            </div>
          )}

          {/* Save Button */}
          <Button
            onClick={handleSave}
            disabled={
              saving ||
              !selectedPackageId ||
              selectedVehicleIds.length === 0 ||
              !preferredDay ||
              !preferredTime
            }
            className="w-full"
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : editingId ? (
              "Update Recurring Setup"
            ) : (
              "Create Recurring Setup"
            )}
          </Button>
        </div>
      ) : null}

      {/* Empty state - show form when no setups exist */}
      {!loading && existingSetups.length === 0 && !isEditing && (
        <div className="text-center py-6">
          <p className="text-sm text-muted-foreground mb-3">
            No recurring appointment setups found
          </p>
          <Button variant="outline" onClick={handleStartNew}>
            <Plus className="h-4 w-4 mr-1" />
            Create First Setup
          </Button>
        </div>
      )}
    </div>
  );
}
